import React from 'react';

import {observer} from 'mobx-react';
import {TextField} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

import {useStores} from '../../../Hooks';
import {permissionsEnum} from '../../../serverClientShared/sharedEnums';
import ConditionalComponent from '../../Shared/ConditionalComponent';
import {OnSwitch} from '../../../assets/live-svg/OnSwitch';
import {OffSwitch} from '../../../assets/live-svg/OffSwitch';
import ThreeDLogoSection from './ThreeDLogoSection';
import RoleVisibilitySection from './RoleVisibilitySection';
import procedureUtility from '../../../Utilities/ProcedureUtility';

const PLAYER_TOOLTIPS = {
    SayNodeTitle: {titleKey: 'AutoNarrateHeader', descKey: 'useTheTextToSpeechToSayTheStepHeaderAutomatically'},
    AutoTTS: {titleKey: 'AutoNarrateDescription', descKey: 'autoTTSDescription'},
    WaitTTSToEndBeforeNext: {title: 'Wait for TTS completion before next step', descKey: 'waitForTextToSpeechToEndBeforeStartingNextStep'},
    VideoAutoPlay: {titleKey: null, descKey: 'videoAutoPlayDescription'},
    ClickToSelect: {title: 'Toggle step advancement on selection', descKey: 'clickToSelectDescription'},
    AllowSkipStep: {titleKey: null, descKey: 'allowSkipStepDescription'},
    ShowIndicationArrows: {titleKey: 'ShowDirectionalArrows', descKey: 'showIndicationArrowsAsHintsForElementsOutsideTheCamera'},
    ForceArMode: {titleKey: 'ArProcedure', descKey: 'OpenProcedureInArMode'},
    ShowProcedureSummary: {title: 'Display quality survey', descKey: 'showQualitySurveyAtTheEndOfTheProcedure'},
};

const THREE_D_TOOLTIPS = {
    AllowSlicer: {titleKey: 'slicer', descKey: 'allowTheSlicerCapability'},
    ShowTooltips: {titleKey: 'toolTips', descKey: 'showPartNameOnToolTips'},
};

const labelClass = 'text-xs font-semibold text-main-normal block mb-0.5';
const rowClass = 'flex items-center justify-between py-1';
const textClass = 'text-xs text-main-normal';
const sectionClass = 'px-5 py-4 flex flex-col gap-3 border-t border-panels-normal first:border-t-0';

const AdvancedSettingsTab = observer(({settings}) => {
    const {generalStore, projectStore} = useStores();
    const t = generalStore.translate.bind(generalStore);
    const {
        procedure,
        editedSettings, editedThreeDSettings, editedPermittedRoles, editedCameraSpeed,
        togglePlayerSetting, toggleThreeDSetting, updateThreeDLogo, togglePermittedRole, updateCameraSpeed,
    } = settings;

    const isDigitalTwin = procedureUtility.isDigitalTwin(procedure);
    const isFldt = procedureUtility.isFldt(procedure);
    const isMedia = !!procedure?.MediaProcedureFile;
    const isRoleVisibilityEnabled = !!generalStore.domainSettings?.IsRoleVisibilityEnabled;
    const canChangePermissions = projectStore.checkAllowedCurrentProject(permissionsEnum.CREATE_PROCEDURE_AND_SETTINGS, false);

    return (
        <div>
            <ConditionalComponent if={isRoleVisibilityEnabled && canChangePermissions}>
                <div className={sectionClass}>
                    <RoleVisibilitySection permittedRoles={editedPermittedRoles} onToggle={togglePermittedRole} t={t} />
                </div>
            </ConditionalComponent>
            <ConditionalComponent if={!isMedia && !isDigitalTwin}>
                <div className={sectionClass}>
                    <PlayerAttributeSection settings={editedSettings} tooltips={PLAYER_TOOLTIPS} onToggle={togglePlayerSetting} t={t} title={t('playerAttributes')} />
                    <CameraSpeedSection value={editedCameraSpeed} onChange={updateCameraSpeed} t={t} />
                </div>
            </ConditionalComponent>
            <ConditionalComponent if={isDigitalTwin && !isFldt}>
                <div className={sectionClass}>
                    <span className={`${labelClass} !mb-1`}>{t('digitalTwinAttributes')}</span>
                    <ThreeDLogoSection logoId={editedThreeDSettings?.Logo} onUpdateLogo={updateThreeDLogo} t={t} />
                    <PlayerAttributeSection settings={editedThreeDSettings} tooltips={THREE_D_TOOLTIPS} onToggle={toggleThreeDSetting} t={t} />
                </div>
            </ConditionalComponent>
        </div>
    );
});

const PlayerAttributeSection = ({settings, tooltips, onToggle, t, title}) => (
    <div>
        <ConditionalComponent if={!!title}>
            <span className={`${labelClass} !mb-1`}>{title}</span>
        </ConditionalComponent>
        {Object.entries(tooltips).map(([key, {title, titleKey, descKey}]) => (
            <div className={rowClass} key={key}>
                <div className="flex items-center gap-1">
                    <span className={textClass}>{title || (titleKey ? t(titleKey) : t(key))}</span>
                    <Tooltip title={t(descKey)} arrow componentsProps={{
                        tooltip: {
                            className: '!text-base !text-main-normal !bg-background-normal !border !border-panels-normal !shadow-lg !rounded-input',
                        },
                    }}>
                        <span className="info-circle !w-4 !h-4 text-[8px] !border-1 border-solid border-main-normal">i</span>
                    </Tooltip>
                </div>
                <div className="cursor-pointer w-12 h-6" data-testid={`toggle-${key}`} data-state={settings?.[key] ? 'on' : 'off'} onClick={() => onToggle(key)}>
                    {settings?.[key]
                        ? <OnSwitch viewBox="0 0 41 20" style={{width: 66, height: 20}} />
                        : <OffSwitch viewBox="0 0 41 20" style={{width: 66, height: 20}} />}
                </div>
            </div>
        ))}
    </div>
);

const CameraSpeedSection = ({value, onChange, t}) => (
    <div className={rowClass}>
        <span className={textClass}>
            {t('AdjustCameraMovementSpeed') + ' (secs) ' + t('BetweenSteps', true)}
        </span>
        <TextField
            type="number"
            variant="outlined"
            size="small"
            value={value}
            onChange={(e) => {
                if (e.target.value === '') return onChange(0);
                const v = Number(e.target.value);
                if (Number.isFinite(v) && v >= 0 && v <= 999) onChange(v);
            }}
            InputProps={{inputProps: {min: 0, max: 999, 'data-testid': 'camera-speed-input'}}}
            style={{width: 80}}
        />
    </div>
);

export default AdvancedSettingsTab;
